import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

class TableOfContents {
  constructor() {
    this.nav = document.querySelector('.table-of-contents');
    this.buttons = [];
    this.sections = [];
    this.isInitialized = false;
    this.scrollHandler = null;
    this.resizeHandler = null;
    this.isScrollingToSection = false; // Flag to disable tracking during page scroll
    this.isMobile = window.innerWidth <= 1024;
    this.init();
  }

  init() {
    if (!this.nav) return;

    // Set up the sticky animation first (this doesn't depend on buttons)
    this.setupStickyAnimation();

    // Wait for dynamic navigation to be populated, then set up the rest
    this.waitForNavigation();

    // Add resize handler for mobile behavior
    this.setupResizeHandler();
  }

  waitForNavigation() {
    // Check if navigation has been populated
    const checkInterval = setInterval(() => {
      const buttons = this.nav.querySelectorAll('.table-of-contents_item');
      if (buttons.length > 0) {
        clearInterval(checkInterval);
        this.buttons = Array.from(buttons);
        this.setupSectionDetection();
        this.setupClickHandlers();
        this.setActiveButton();
        this.isInitialized = true;

        // Check if we need to apply initial sticky state after buttons are loaded
        this.checkInitialStickyState();

        // Update mobile body padding
        this.updateMobileBodyPadding();
      }
    }, 100); // Check every 100ms

    // Stop checking after 10 seconds to prevent infinite checking
    setTimeout(() => {
      clearInterval(checkInterval);
    }, 10000);
  }

  setupStickyAnimation() {
    // Use ScrollTrigger to control the sticky state
    ScrollTrigger.create({
      trigger: this.nav,
      start: 'top top',
      end: 'max', // This ensures it stays sticky until the page ends
      onEnter: () => this.onStickyEnter(),
      onLeave: () => this.onStickyLeave(),
      onEnterBack: () => this.onStickyEnter(),
      onLeaveBack: () => this.onStickyLeave(),
    });

    // Add a scroll listener to handle edge cases when scrolling to the very bottom
    this.scrollHandler = () => {
      const navRect = this.nav.getBoundingClientRect();
      const isAtTop = navRect.top <= 0;

      // If the nav is at the top (sticky position), ensure it has the sticky class
      if (isAtTop) {
        this.nav.classList.add('is-sticky');
      }
    };

    window.addEventListener('scroll', this.scrollHandler);

    // Check initial scroll position and apply sticky state if needed
    this.checkInitialStickyState();
  }

  checkInitialStickyState() {
    // Check if the navigation is already in sticky position on page load
    const navRect = this.nav.getBoundingClientRect();
    const isAlreadySticky = navRect.top <= 0;

    if (isAlreadySticky) {
      // Apply sticky state immediately without animation
      this.nav.classList.add('is-sticky');
    }
  }

  onStickyEnter() {
    this.nav.classList.add('is-sticky');
  }

  onStickyLeave() {
    this.nav.classList.remove('is-sticky');
  }

  setupSectionDetection() {
    // Get all sections that correspond to navigation links
    this.buttons.forEach((button) => {
      const link = button.querySelector('a');
      if (link) {
        const href = link.getAttribute('href');
        if (href && href.startsWith('#')) {
          const section = document.querySelector(href);
          if (section) {
            this.sections.push({
              element: section,
              button: button,
              id: href,
            });
          }
        }
      }
    });

    // Set up ScrollTrigger for each section
    this.sections.forEach((section) => {
      ScrollTrigger.create({
        trigger: section.element,
        start: 'top center',
        end: 'bottom center',
        onEnter: () => {
          if (!this.isScrollingToSection) {
            this.setActiveButton(section.button);
          }
        },
        onEnterBack: () => {
          if (!this.isScrollingToSection) {
            this.setActiveButton(section.button);
          }
        },
      });
    });
  }

  setupClickHandlers() {
    this.buttons.forEach((button) => {
      const link = button.querySelector('a');
      if (link) {
        link.addEventListener('click', (e) => {
          const href = link.getAttribute('href');
          if (href && href.startsWith('#')) {
            e.preventDefault();

            // On mobile, set the active button first and then scroll to it
            if (this.isMobile) {
              this.setActiveButton(button, true); // Skip automatic scroll
              this.scrollToActiveItem(button); // Manual scroll with better control
            } else {
              this.setActiveButton(button);
            }

            this.scrollToSection(href);
          }
        });
      }
    });
  }

  scrollToSection(hash) {
    const target = document.querySelector(hash);
    if (target) {
      const navHeight = this.nav.offsetHeight;
      const targetPosition = target.offsetTop - navHeight;

      // Set flag to disable scroll tracking during animation
      this.isScrollingToSection = true;

      // Safety timeout to re-enable tracking in case animation fails
      const safetyTimeout = setTimeout(() => {
        this.isScrollingToSection = false;
        this.setActiveButton();
      }, 1000); // 1 second timeout

      gsap.to(window, {
        duration: 0.8,
        scrollTo: { y: targetPosition, autoKill: false },
        ease: 'power2.inOut',
        onComplete: () => {
          // Clear safety timeout
          clearTimeout(safetyTimeout);
          // Re-enable scroll tracking after animation completes
          this.isScrollingToSection = false;
          // Set the correct active button after animation
          this.setActiveButton();
        },
      });
    }
  }

  setActiveButton(activeButton = null, skipScroll = false) {
    // Don't update if we're currently scrolling to a section (unless explicitly called)
    if (this.isScrollingToSection && !activeButton) {
      return;
    }

    // Remove active class from all buttons
    this.buttons.forEach((button) => {
      button.classList.remove('active');
    });

    // Add active class to the specified button or find the current section
    if (activeButton) {
      activeButton.classList.add('active');
      if (!skipScroll) {
        this.scrollToActiveItem(activeButton);
      }
    } else {
      // Find which section is currently in view
      const scrollPosition = window.scrollY + this.nav.offsetHeight + 100;

      for (let i = this.sections.length - 1; i >= 0; i--) {
        const section = this.sections[i];
        if (section.element.offsetTop <= scrollPosition) {
          section.button.classList.add('active');
          if (!skipScroll) {
            this.scrollToActiveItem(section.button);
          }
          break;
        }
      }
    }
  }

  scrollToActiveItem(activeButton) {
    // Only apply mobile scrolling behavior on mobile devices
    if (this.isMobile) {
      const navList = this.nav.querySelector('.table-of-contents_list');
      if (navList && activeButton) {
        // Find the index of the active button
        const activeIndex = this.buttons.indexOf(activeButton);
        if (activeIndex === -1) return;

        // Small delay to ensure DOM has updated
        requestAnimationFrame(() => {
          // Calculate the scroll position based on the button's position in the list
          let scrollLeft = 0;
          for (let i = 0; i < activeIndex; i++) {
            const button = this.buttons[i];
            const buttonWidth = button.offsetWidth;
            const gap = 16; // gap-4 equivalent (1rem = 16px)
            scrollLeft += buttonWidth + gap;
          }

          // Use GSAP for smooth, controlled animation with faster duration
          gsap.to(navList, {
            scrollLeft: scrollLeft,
            duration: 0.2,
            ease: 'power2.out',
          });
        });
      }
    }
  }

  setupResizeHandler() {
    let resizeTimeout;
    this.resizeHandler = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        // Refresh ScrollTrigger on resize
        ScrollTrigger.refresh();

        // Update mobile body padding
        this.updateMobileBodyPadding();

        // Recalculate active button position on mobile
        if (this.isMobile && this.isInitialized) {
          this.setActiveButton();
        }
      }, 250);
    };

    window.addEventListener('resize', this.resizeHandler);
  }

  // Function to collect navigation items from blocks with data-anchor-name
  collectNavigationItems() {
    const navItems = [];

    // Collect all elements with data-anchor-name attribute
    const allElements = [];
    const elements = document.querySelectorAll('[data-anchor-name]');

    elements.forEach((element) => {
      const anchorName = element.getAttribute('data-anchor-name');
      const anchorId = element.getAttribute('id');

      if (anchorName && anchorId) {
        allElements.push({
          element: element,
          anchor: anchorId,
          label: anchorName,
          position: element.offsetTop, // Use offsetTop to get vertical position
        });
      }
    });

    // Sort by DOM position (top to bottom)
    allElements.sort((a, b) => a.position - b.position);

    // Extract just the navigation data
    return allElements.map((item) => ({
      anchor: item.anchor,
      label: item.label,
    }));
  }

  // Generate navigation HTML
  generateNavigation(navItems) {
    const navList = document.getElementById('toc-list');
    const navContainer = document.getElementById('toc-container');

    if (!navList) return;

    // Clear existing content
    navList.innerHTML = '';

    if (navItems.length === 0) {
      // Hide the navigation if no items
      if (navContainer) {
        navContainer.classList.add('is-hidden');
      }
      return;
    }

    // Show the navigation container
    if (navContainer) {
      navContainer.classList.remove('is-hidden');
    }

    // Generate navigation items
    navItems.forEach((item) => {
      const li = document.createElement('li');
      li.className = 'table-of-contents_item';

      const a = document.createElement('a');
      a.href = '#' + item.anchor;
      a.textContent = item.label;

      li.appendChild(a);
      navList.appendChild(li);
    });

    // Update buttons array and refresh navigation
    this.buttons = Array.from(navList.querySelectorAll('.table-of-contents_item'));
    this.refreshNavigation();
  }

  // Method to refresh navigation when new buttons are added
  refreshNavigation() {
    if (!this.nav) return;

    // Get updated buttons
    this.buttons = Array.from(this.nav.querySelectorAll('.table-of-contents_item'));

    if (this.buttons.length > 0) {
      // Clear existing sections and ScrollTriggers
      this.sections.forEach((section) => {
        ScrollTrigger.getAll().forEach((trigger) => {
          if (trigger.trigger === section.element) {
            trigger.kill();
          }
        });
      });
      this.sections = [];

      // Set up new sections and click handlers
      this.setupSectionDetection();
      this.setupClickHandlers();
      this.setActiveButton();

      // Refresh ScrollTrigger
      ScrollTrigger.refresh();
    }

    // Update mobile body padding
    this.updateMobileBodyPadding();
  }

  updateMobileBodyPadding() {
    if (this.isMobile) {
      const navHeight = this.nav.offsetHeight;
      if (navHeight > 0) {
        document.body.style.paddingBottom = navHeight + 'px';
      }
    } else {
      document.body.style.paddingBottom = '';
    }
  }

  // Method to refresh ScrollTriggers (useful for dynamic content)
  refresh() {
    ScrollTrigger.refresh();
  }

  // Method to destroy the instance
  destroy() {
    ScrollTrigger.getAll().forEach((trigger) => {
      if (
        trigger.trigger === this.nav ||
        this.sections.some((s) => s.element === trigger.trigger)
      ) {
        trigger.kill();
      }
    });

    // Remove the scroll listener
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }

    // Remove resize listener
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }
}

// Initialize when DOM is ready
let tableOfContentsInstance = null;

document.addEventListener('DOMContentLoaded', () => {
  tableOfContentsInstance = new TableOfContents();

  // Set up dynamic content scanning
  let scanCount = 0;
  const maxScans = 10; // Prevent infinite scanning
  const scanInterval = setInterval(() => {
    scanCount++;
    const newNavItems = tableOfContentsInstance.collectNavigationItems();

    // If we found items or reached max scans, stop scanning
    if (newNavItems.length > 0 || scanCount >= maxScans) {
      clearInterval(scanInterval);
      if (newNavItems.length > 0) {
        tableOfContentsInstance.generateNavigation(newNavItems);
      }
    }
  }, 500); // Check every 500ms
});

// Make the instance available globally
window.TableOfContents = {
  getInstance: () => tableOfContentsInstance,
  refresh: () => tableOfContentsInstance?.refreshNavigation(),
};

export default TableOfContents;
